import { fetchMyBuilds } from '@/api/builds';
import {
	fetchLandingMetaPixels,
	fetchLandingSites,
	fetchLandingTemplates,
	fetchLandingUploadedBuilds,
	patchLandingLink,
} from '@/api/landings';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import type { IBuild } from '@/entities/build.entity';
import type {
	ILandingLink,
	ILandingMetaPixel,
	ILandingSite,
	ILandingTemplate,
	ILandingUploadedBuild,
	LandingDownloadTrigger,
} from '@/entities/landing.entity';
import { formatMetaPixelOptionLabel } from '@/entities/landing.entity';
import CopyableUrlRow from '@/pages/landings/components/copyable-url-row';
import LandingApkPicker from '@/pages/landings/components/landing-apk-picker';
import DownloadTriggerSelect from '@/pages/landings/components/download-trigger-select';
import { hostnameFromLandingUrl } from '@/pages/landings/lib/landing-url';
import {
	isTelegramAuthLandingTemplate,
	isTelegramSite,
} from '@/pages/landings/lib/landing-site-mode';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

interface EditLinkModalProps {
	link: ILandingLink | null;
	onOpenChange: (open: boolean) => void;
	onUpdated: () => void;
}

function formatBuildLabel(build: IBuild): string {
	const name = build.appName ?? build.id;
	const date = new Date(build.createdAt).toLocaleDateString('ru-RU');
	return `${name} · ${date}`;
}

function formatDateTime(value: string): string {
	return new Date(value).toLocaleString('ru-RU');
}

function resolveTelegramMode(link: ILandingLink, site: ILandingSite | undefined): boolean {
	if (site) {
		return isTelegramSite(site);
	}
	return link.tgUrl != null;
}

export default function EditLinkModal({ link, onOpenChange, onUpdated }: EditLinkModalProps) {
	const open = link !== null;

	const [sites, setSites] = useState<ILandingSite[]>([]);
	const [templates, setTemplates] = useState<ILandingTemplate[]>([]);
	const [builds, setBuilds] = useState<IBuild[]>([]);
	const [uploadedBuilds, setUploadedBuilds] = useState<ILandingUploadedBuild[]>([]);
	const [pixels, setPixels] = useState<ILandingMetaPixel[]>([]);
	const [buildId, setBuildId] = useState('');
	const [tgUrl, setTgUrl] = useState('');
	const [metaPixelId, setMetaPixelId] = useState('none');
	const [downloadTrigger, setDownloadTrigger] = useState<LandingDownloadTrigger>('cta');
	const [loadingMeta, setLoadingMeta] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const linkSite = useMemo(
		() => (link ? sites.find(s => s.id === link.siteId) : undefined),
		[sites, link],
	);
	const linkTemplate = useMemo(
		() => (link ? templates.find(t => t.slug === link.templateSlug) : undefined),
		[templates, link],
	);
	const telegramAuthLanding = link
		? isTelegramAuthLandingTemplate(linkTemplate, link.templateSlug)
		: false;
	const telegramMode = link ? resolveTelegramMode(link, linkSite) : false;
	const telegramRedirectMode = telegramMode && !telegramAuthLanding;

	useEffect(() => {
		if (!link) return;
		const activeLink = link;
		let cancelled = false;

		async function loadMeta() {
			setLoadingMeta(true);
			try {
				const [siteItems, buildItems, pixelItems, uploadedItems, templateItems] = await Promise.all([
					fetchLandingSites(),
					fetchMyBuilds(),
					fetchLandingMetaPixels(),
					fetchLandingUploadedBuilds().catch(() => [] as ILandingUploadedBuild[]),
					fetchLandingTemplates(),
				]);
				if (cancelled) return;
				setSites(siteItems);
				setTemplates(templateItems);
				const readyBuilds = buildItems.filter(b => b.hasApk && b.successBuilded);
				const currentBuild = buildItems.find(b => b.id === activeLink.buildId);
				const displayBuilds =
					currentBuild && !readyBuilds.some(b => b.id === activeLink.buildId)
						? [currentBuild, ...readyBuilds]
						: readyBuilds;
				setBuilds(displayBuilds);
				setUploadedBuilds(uploadedItems);
				setPixels(pixelItems);
				setBuildId(activeLink.buildId ?? '');
				setTgUrl(activeLink.tgUrl ?? '');
				setMetaPixelId(activeLink.metaPixel?.id != null ? String(activeLink.metaPixel.id) : 'none');
				setDownloadTrigger(activeLink.downloadTrigger ?? 'cta');
			} finally {
				if (!cancelled) setLoadingMeta(false);
			}
		}

		void loadMeta();
		return () => {
			cancelled = true;
		};
	}, [link]);

	function handleOpenChange(next: boolean) {
		if (!next) {
			setBuildId('');
			setTgUrl('');
			setMetaPixelId('none');
			setDownloadTrigger('cta');
			setUploadedBuilds([]);
		}
		onOpenChange(next);
	}

	function handleCopy(text: string) {
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		if (!link) return;

		const patch: {
			buildId?: string;
			tgUrl?: string;
			metaPixelId?: number | null;
			downloadTrigger?: LandingDownloadTrigger;
		} = {};

		if (telegramRedirectMode) {
			const trimmedTgUrl = tgUrl.trim();
			if (!trimmedTgUrl) {
				toast.error('Укажите URL Telegram');
				return;
			}
			if (trimmedTgUrl !== (link.tgUrl ?? '')) {
				patch.tgUrl = trimmedTgUrl;
			}
		} else if (!telegramAuthLanding && buildId && buildId !== link.buildId) {
			patch.buildId = buildId;
		}

		const currentDownloadTrigger = link.downloadTrigger ?? 'cta';
		if (!telegramMode && !telegramAuthLanding && downloadTrigger !== currentDownloadTrigger) {
			patch.downloadTrigger = downloadTrigger;
		}

		const currentPixelId = link.metaPixel?.id ?? null;
		const nextPixelId = metaPixelId === 'none' ? null : Number(metaPixelId);
		if (nextPixelId !== currentPixelId) {
			patch.metaPixelId = nextPixelId;
		}

		if (Object.keys(patch).length === 0) {
			toast.message('Нет изменений');
			return;
		}

		setIsSaving(true);
		try {
			const result = await patchLandingLink(link.id, patch);
			const count = result.updatedLinkIds.length;
			if (count > 1) {
				toast.success(`Обновлено ${count} ссылок воронки`);
			} else {
				toast.success('Ссылка обновлена');
			}
			handleOpenChange(false);
			onUpdated();
		} finally {
			setIsSaving(false);
		}
	}

	if (!link) {
		return null;
	}

	const hostname = hostnameFromLandingUrl(link.url);
	const currentBuild = builds.find(b => b.id === link.buildId);
	const currentUploaded = uploadedBuilds.find(b => b.buildId === link.buildId);
	const currentBuildLabel = currentBuild
		? formatBuildLabel(currentBuild)
		: currentUploaded
			? currentUploaded.appName?.trim() || currentUploaded.apkFileName || currentUploaded.buildId
			: link.buildId ?? '—';

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className='flex w-full min-w-0 max-h-[85vh] flex-col overflow-hidden gap-0 p-0 sm:max-w-[480px]'>
				<DialogHeader className='shrink-0 px-6 pt-6 pb-2'>
					<DialogTitle>Ссылка</DialogTitle>
					<DialogDescription>
						{telegramAuthLanding
							? 'Только Meta Pixel, без APK и Telegram URL.'
							: telegramMode
								? 'Информация о ссылке и настройки Telegram / Meta Pixel.'
								: 'Информация о ссылке и настройки APK / Meta Pixel.'}
					</DialogDescription>
				</DialogHeader>

				{loadingMeta ? (
					<div className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 pb-6'>
						<div className='flex flex-col gap-4'>
							<Skeleton className='h-10 w-full' />
							<Skeleton className='h-10 w-full' />
							<Skeleton className='h-10 w-full' />
						</div>
					</div>
				) : (
					<form className='flex min-h-0 flex-1 flex-col overflow-hidden' onSubmit={handleSubmit}>
						<div className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 pb-2'>
							<div className='flex min-w-0 flex-col gap-4'>
								<div className='flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-muted/20 p-3 text-sm'>
									<div className='flex flex-col gap-0.5'>
										<span className='text-xs text-muted-foreground'>ID</span>
										<span className='font-mono text-xs'>{link.id}</span>
									</div>
									<CopyableUrlRow label='URL' url={link.url} onCopy={handleCopy} />
									<div className='grid grid-cols-2 gap-3'>
										<div className='flex flex-col gap-0.5 min-w-0'>
											<span className='text-xs text-muted-foreground'>Домен</span>
											<span className='truncate'>{hostname}</span>
										</div>
										<div className='flex flex-col gap-0.5 min-w-0'>
											<span className='text-xs text-muted-foreground'>Шаблон</span>
											<span className='truncate'>{link.templateSlug}</span>
										</div>
										<div className='flex flex-col gap-0.5 min-w-0'>
											<span className='text-xs text-muted-foreground'>Создана</span>
											<span>{formatDateTime(link.createdAt)}</span>
										</div>
										{link.expiresAt ? (
											<div className='flex flex-col gap-0.5 min-w-0'>
												<span className='text-xs text-muted-foreground'>Истекает</span>
												<span>{formatDateTime(link.expiresAt)}</span>
											</div>
										) : null}
									</div>
									{link.metaPixel ? (
										<div className='flex flex-col gap-0.5 min-w-0'>
											<span className='text-xs text-muted-foreground'>Текущий Meta Pixel</span>
											<span>
												{formatMetaPixelOptionLabel({
													name: link.metaPixel.name,
													conversionEvent: link.metaPixel.conversionEvent,
												})}
											</span>
										</div>
									) : null}
									{telegramRedirectMode ? (
										<div className='flex flex-col gap-0.5 min-w-0'>
											<span className='text-xs text-muted-foreground'>Текущий Telegram URL</span>
											<span className='truncate' title={link.tgUrl ?? undefined}>
												{link.tgUrl ?? '—'}
											</span>
										</div>
									) : telegramAuthLanding ? null : (
										<div className='flex flex-col gap-0.5 min-w-0'>
											<span className='text-xs text-muted-foreground'>Текущая сборка</span>
											<span className='truncate' title={link.buildId ?? undefined}>
												{currentBuildLabel}
											</span>
										</div>
									)}
								</div>

								{telegramRedirectMode ? (
									<div className='flex flex-col gap-1.5'>
										<Label htmlFor='edit-landing-tg-url'>URL Telegram</Label>
										<InputGroup>
											<InputGroupInput
												id='edit-landing-tg-url'
												placeholder='https://t.me/your_channel'
												value={tgUrl}
												onChange={e => setTgUrl(e.target.value)}
												disabled={isSaving}
											/>
										</InputGroup>
									</div>
								) : telegramAuthLanding ? null : (
									<>
										<LandingApkPicker
											panelBuilds={builds}
											uploadedBuilds={uploadedBuilds}
											buildId={buildId}
											onBuildIdChange={setBuildId}
											onUploaded={uploaded => {
												setUploadedBuilds(prev =>
													prev.some(b => b.buildId === uploaded.buildId)
														? prev
														: [uploaded, ...prev],
												);
												setBuildId(uploaded.buildId);
											}}
											disabled={isSaving}
										/>
										<DownloadTriggerSelect
											value={downloadTrigger}
											onValueChange={setDownloadTrigger}
											disabled={isSaving}
										/>
									</>
								)}

								<div className='flex flex-col gap-1.5'>
									<Label>Meta Pixel</Label>
									<Select value={metaPixelId} onValueChange={setMetaPixelId}>
										<SelectTrigger>
											<SelectValue placeholder='Без пикселя' />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value='none'>Без пикселя</SelectItem>
											{pixels.map(pixel => (
												<SelectItem key={pixel.id} value={String(pixel.id)}>
													{formatMetaPixelOptionLabel(pixel)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
						</div>

						<DialogFooter className='shrink-0 border-t border-border px-6 py-4 flex-col sm:flex-col'>
							<div className='w-full'>
								<LoadingWrapper show={isSaving}>
									<Button type='submit' className='w-full' disabled={isSaving}>
										Сохранить
									</Button>
								</LoadingWrapper>
							</div>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}
